import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { connectHits } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';

import SearchState from '../../../../algolia-search/js/components/elements/SearchState';
import PagingToolbar from '../../../../algolia-search/js/components/sections/PagingToolbar';
import useAuthorized from '@hooks/useAuthorized';
import { getAlgoliaIndexNameGlobal } from '@general/helpers/search.helpers';
import useIcscTracking from '@tracking/useIcscTracking';

const Container = styled.div`
    @media (min-width: 1200px) {
        margin: 0 -12px;
    }
`;

const SearchItemStyled = styled.div`
    width: 100%;

    @media (min-width: 1200px) {
        margin: 0 12px;

        &.one-per-row {
            flex: 0 0 100%;
            max-width: calc(100% - 24px);
        }

        &.two-per-row {
            flex: 0 0 50%;
            max-width: calc(50% - 24px);
        }

        &.three-per-row {
            flex: 0 0 33.33333%;
            max-width: calc(33.33333% - 24px);
        }
    }
`;

const SearchHits = ({
    HitComponent,
    NoResultsText,
    displayPagingToolbar = false,
    favoritesProp,
    favoritesQuery,
    hitProps = {},
    index,
    resultsPerRow = 3,
    searchType,
    shouldLogConversions = false,
    hits,
    getIsSelectedFunc,
    transformFunc,
    onDelete,
    onToggleSelected,
}) => {
    const indexName = getAlgoliaIndexNameGlobal(index);

    // -----------------------------
    // State
    // -----------------------------

    const { isLoggedIn } = useAuthorized();
    const { data: savedRecordsData } = useQuery(favoritesQuery, { skip: !isLoggedIn });

    const favoriteIds = useMemo(() => {
        if (!savedRecordsData || !savedRecordsData[favoritesProp]) {
            return [];
        }

        return savedRecordsData[favoritesProp].filter(f => !!f).map(f => f.recordId);
    }, [savedRecordsData, favoritesProp]);

    const { trackEvent } = useIcscTracking();

    // -----------------------------
    // Functionality
    // -----------------------------

    // const handleLogConversion = hit => {
    //     if (!shouldLogConversions) {
    //         return;
    //     }

    // trackEvent({
    //     click: {
    //         type: 'conversion',
    //         index: `${process.env.ALGOLIA_ENV_PREFIX}_${index}`,
    //         hit,
    //         target: 'favorited_algolia_search_hit',
    //         value: getEventName(),
    //     },
    // });
    // };

    // const getEventName = () => {
    //     let eventName = '';

    //     if (parentView === 'Connections') {
    //         eventName = parentSubview;
    //     } else if (parentView === 'Events') {
    //         if (index === 'members' || index === 'saved_searchable_members') {
    //             eventName = eventNames.EVENT_CONNECT;
    //         } else if (index === 'properties') {
    //             eventName = eventNames.EVENT_PROPERTIES;
    //         }
    //     } else if (parentView === 'Properties') {
    //         eventName = parentView;
    //     }

    //     return eventName;
    // };

    const getIsFavorite = hit => {
        if (searchType === 'properties') {
            return favoriteIds && favoriteIds.includes(hit.recordId);
        }

        if (searchType === 'saved_searchable_members') {
            return favoriteIds && favoriteIds.includes(hit.memberId);
        }

        const id = parseInt(hit.objectID.split('-')[1], 10);
        return favoriteIds && favoriteIds.includes(id);
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (hits.length === 0) {
        return <SearchState hitCount={hits.length} NoResultsText={NoResultsText} />;
    }

    return (
        <Container className="d-flex flex-wrap">
            {hits.map(hit => (
                <SearchItemStyled
                    key={hit.objectID}
                    className={classnames('d-flex flex-column mb-1.5rem', {
                        'one-per-row': resultsPerRow === 1,
                        'two-per-row': resultsPerRow === 2,
                        'three-per-row': resultsPerRow === 3,
                    })}
                >
                    <HitComponent
                        {...transformFunc({ hit })}
                        isFavorite={getIsFavorite(hit)}
                        isSelected={getIsSelectedFunc ? getIsSelectedFunc(hit) : null}
                        // favorited={handleLogConversion}
                        // unfavorited={removed}
                        hit={hit}
                        onClick={() =>
                            trackEvent({
                                click: {
                                    index: indexName,
                                    hit,
                                    target: 'click_search_result',
                                    value: 'Click Search Hit',
                                },
                            })
                        }
                        onDelete={onDelete}
                        // onMessageSent={onMessageSent}
                        onToggleSelected={onToggleSelected}
                        {...hitProps}
                    />
                </SearchItemStyled>
            ))}

            {displayPagingToolbar && (
                <div className="d-flex justify-content-center mt-1rem w-full">
                    <PagingToolbar scrollToRef={null} />
                </div>
            )}
        </Container>
    );
};

SearchHits.propTypes = {
    HitComponent: PropTypes.elementType.isRequired,
    NoResultsText: PropTypes.elementType,
    displayPagingToolbar: PropTypes.bool,
    favoritesProp: PropTypes.string,
    favoritesQuery: PropTypes.object,
    hitProps: PropTypes.object,
    index: PropTypes.string.isRequired,
    resultsPerRow: PropTypes.number,
    searchType: PropTypes.string.isRequired,
    shouldLogConversions: PropTypes.bool,
    hits: PropTypes.array.isRequired,
    getIsSelectedFunc: PropTypes.func,
    transformFunc: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    onToggleSelected: PropTypes.func,
};

export default connectHits(SearchHits);
